import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, FileDown, Loader2, Pencil, Save, X, Sparkles, Download, DollarSign, TrendingDown, Wallet, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { releaseStatementLoads } from '../../lib/releaseStatementLoads';
import { useToast } from '../../contexts/ToastContext';
import { build1099NecPdfBytes } from '../../utils/nec1099PdfFill';

// PostgREST returns a variety of codes when a queried column or table isn't
// present yet (pending migration, stale schema cache, etc). Treat all of them
// as "missing" so we can fall back to a narrower column set.
function isMissingColumnErr(err: any): boolean {
  if (!err) return false;
  const code = err.code;
  return code === '42703' || code === '42P01' || code === 'PGRST204' || code === 'PGRST205';
}

// Remember whether the extended columns are present on this Supabase instance
// so we don't send a doomed 400-bound request on every navigation. The flags
// start as null (= unknown), flip to true on first success, or false the first
// time we see a missing-column error.
let hasStatementExtendedCols: boolean | null = null;
let hasStatementMidCols: boolean | null = null;
let hasTaxInfoSplitCols: boolean | null = null;

interface DriverStatementRow {
  id: string;
  driver_name: string;
  truck_number: string;
  week_start_date: string;
  week_end_date: string;
  total_revenue?: number | null;
  total_deductions?: number | null;
  total_fuel?: number | null;
  total_credits?: number | null;
  net_pay: number;
  contractor_name?: string | null;
  imported_file_name?: string | null;
  imported_file_path?: string | null;
  created_at: string;
}

interface Company1099DriverDetailViewProps {
  driverName: string;
  onBack?: () => void;
}

export function Company1099DriverDetailView({ driverName, onBack }: Company1099DriverDetailViewProps) {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [statements, setStatements] = useState<DriverStatementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState<{
    label: string;
    url: string;
    bytes: Uint8Array;
  } | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Separate modal for previewing an individual imported statement PDF.
  const [statementPreview, setStatementPreview] = useState<{
    url: string;
    row: DriverStatementRow;
  } | null>(null);
  const [statementPreviewLoading, setStatementPreviewLoading] = useState<string | null>(null);

  // Tax info (TIN, street, city, state, zip) — keyed by driver_name.
  // City/state/zip are stored separately so each lands in its own PDF box.
  const [taxInfo, setTaxInfo] = useState<{
    recipient_tin: string;
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
  }>({ recipient_tin: '', street_address: '', city: '', state: '', zip_code: '' });
  const [editingTax, setEditingTax] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  // PAYER info resolved from the driver's company (drivers.company_id → companies).
  // Used on the printed 1099-NEC PDF.
  const [payerInfo, setPayerInfo] = useState<{
    payer_tin?: string | null;
    name?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    phone?: string | null;
  } | null>(null);
  // Contractor / Owner Operator info pulled from the driver's most recent
  // scheduled_deductions entry. Drives the driverType classification (Owner
  // Operator vs Company Driver) and seeds the 1099-NEC business name.
  const [contractorInfo, setContractorInfo] = useState<{
    id?: string;
    lease_or_rent: string;
    contractor_name?: string | null;
    contractor_address?: string | null;
    contractor_tin?: string | null;
    commission_cpm?: string | null;
  } | null>(null);
  const [editingContractor, setEditingContractor] = useState(false);
  const [savingContractor, setSavingContractor] = useState(false);
  const [contractorDraft, setContractorDraft] = useState<{
    contractor_name: string;
    contractor_tin: string;
    contractor_address: string;
  }>({ contractor_name: '', contractor_tin: '', contractor_address: '' });
  // Whether the generated 1099-NEC PDF should use the contractor's name/TIN
  // as the RECIPIENT instead of the driver's personal name. Persisted on
  // driver_1099_info.use_contractor_on_1099 so it sticks across sessions.
  const [useContractorOn1099, setUseContractorOn1099] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [driverName, year]);

  async function loadAll() {
    try {
      setLoading(true);
      // Reset contractor state so we don't carry values from a previous
      // driver/year into this load.
      setContractorInfo(null);
      setContractorDraft({ contractor_name: '', contractor_tin: '', contractor_address: '' });
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      // Statements — request the richest available column set. On first run
      // we probe the rich set; once we learn a group of columns is missing
      // we cache it in a module-level flag and skip that SELECT thereafter.
      // Skip imported_file_* AND contractor_name in the SELECT — both can be
      // missing from PostgREST's schema cache on managed Supabase even when
      // they exist in Postgres. We fill imported_file_* from the storage
      // listing below, and driverType is resolved separately by querying
      // scheduled_deductions for this driver.
      const baseCols = 'id, driver_name, truck_number, week_start_date, week_end_date, net_pay, created_at';
      const midCols = `${baseCols}, total_revenue, total_deductions, total_fuel, total_credits`;
      const richCols = midCols;
      const queryStatements = (cols: string) => supabase
        .from('driver_statements')
        .select(cols)
        .ilike('driver_name', driverName.trim())
        .gte('week_start_date', yearStart)
        .lte('week_end_date', yearEnd)
        .order('week_start_date', { ascending: true });
      let sRes: any;
      if (hasStatementExtendedCols === false && hasStatementMidCols === false) {
        sRes = await queryStatements(baseCols);
      } else if (hasStatementExtendedCols === false) {
        sRes = await queryStatements(midCols);
      } else {
        sRes = await queryStatements(richCols);
        if (!sRes.error) hasStatementExtendedCols = true;
      }
      if (isMissingColumnErr(sRes.error)) {
        hasStatementExtendedCols = false;
        sRes = await queryStatements(midCols);
        if (!sRes.error) hasStatementMidCols = true;
      }
      if (isMissingColumnErr(sRes.error)) {
        hasStatementMidCols = false;
        sRes = await queryStatements(baseCols);
      }
      if (sRes.error) throw sRes.error;
      const loadedStatements = (sRes.data || []) as DriverStatementRow[];

      // Bypass for stale PostgREST schema cache: even if the driver_statements
      // row doesn't come back with imported_file_path (because PostgREST's
      // cache hasn't picked up the new column), we can still locate the source
      // PDF by listing the storage bucket. Filenames are prefixed with the
      // Date.now() ms-timestamp used at upload, which lines up to within
      // seconds with the row's created_at.
      try {
        const needLookup = loadedStatements.some(r => !r.imported_file_path);
        if (needLookup) {
          const yearPath = `1099/imports/${year}`;
          const { data: files } = await supabase.storage
            .from('company-documents')
            .list(yearPath, { limit: 1000 });
          if (files && files.length) {
            type FileEntry = { path: string; name: string; ts: number };
            const indexed: FileEntry[] = [];
            for (const f of files) {
              const match = f.name.match(/^(\d{10,13})-(.+)$/);
              if (!match) continue;
              const ts = Number(match[1]);
              if (!Number.isFinite(ts)) continue;
              indexed.push({ path: `${yearPath}/${f.name}`, name: match[2], ts });
            }
            for (const row of loadedStatements) {
              if (row.imported_file_path) continue;
              const rowTs = new Date(row.created_at).getTime();
              // Pick the closest file within a 15-minute window.
              let best: FileEntry | null = null;
              let bestDelta = 15 * 60 * 1000;
              for (const f of indexed) {
                const d = Math.abs(f.ts - rowTs);
                if (d < bestDelta) { best = f; bestDelta = d; }
              }
              if (best) {
                row.imported_file_path = best.path;
                row.imported_file_name = best.name;
              }
            }
          }
        }
      } catch (err) {
        console.warn('Storage-bucket fallback for statement PDFs failed:', err);
      }

      setStatements(loadedStatements);

      // Tax info — prefer the split city/state/zip columns, fall back to the
      // legacy city_state_zip string if the newer migration isn't deployed.
      // Skip the rich probe if we already learned it's missing.
      const emptyTax = { recipient_tin: '', street_address: '', city: '', state: '', zip_code: '' };
      const richTaxCols = 'recipient_tin, street_address, city, state, zip_code, city_state_zip, use_contractor_on_1099, contractor_name, contractor_tin, contractor_address';
      const legacyTaxCols = 'recipient_tin, street_address, city_state_zip';
      let tRes: any;
      if (hasTaxInfoSplitCols === false) {
        tRes = await supabase
          .from('driver_1099_info')
          .select(legacyTaxCols)
          .ilike('driver_name', driverName.trim())
          .limit(1)
          .maybeSingle();
      } else {
        tRes = await supabase
          .from('driver_1099_info')
          .select(richTaxCols)
          .ilike('driver_name', driverName.trim())
          .limit(1)
          .maybeSingle();
        if (!tRes.error) hasTaxInfoSplitCols = true;
      }
      if (isMissingColumnErr(tRes.error)) {
        hasTaxInfoSplitCols = false;
        tRes = await supabase
          .from('driver_1099_info')
          .select(legacyTaxCols)
          .ilike('driver_name', driverName.trim())
          .limit(1)
          .maybeSingle();
      }
      if (tRes.error) {
        const code = (tRes.error as any).code;
        if (code !== '42P01' && code !== 'PGRST205') {
          console.error('Failed to load 1099 info:', tRes.error);
        }
        setTaxInfo(emptyTax);
      } else if (tRes.data) {
        const t: any = tRes.data;
        // If split columns are empty but legacy city_state_zip has a value,
        // parse it into city/state/zip on the fly (best-effort).
        let city = t.city || '';
        let state = t.state || '';
        let zip_code = t.zip_code || '';
        if (!city && !state && !zip_code && t.city_state_zip) {
          // Try "City, ST 12345" or "City, ST, 12345" or "City, ST USA 12345"
          const m = String(t.city_state_zip).match(/^\s*([^,]+?)\s*,\s*([A-Za-z]{2})\b(?:\s*,?\s*(?:USA|US))?\s*,?\s*(\d{5}(?:-\d{4})?)?/);
          if (m) {
            city = m[1] || '';
            state = (m[2] || '').toUpperCase();
            zip_code = m[3] || '';
          }
        }
        setTaxInfo({
          recipient_tin: t.recipient_tin || '',
          street_address: t.street_address || '',
          city,
          state,
          zip_code,
        });
        setUseContractorOn1099(!!t.use_contractor_on_1099);
        // Seed contractor fields from driver_1099_info (primary source).
        const cName = (t.contractor_name || '').toString();
        const cTin = (t.contractor_tin || '').toString();
        const cAddr = (t.contractor_address || '').toString();
        if (cName || cTin || cAddr) {
          setContractorInfo(prev => ({
            id: prev?.id,
            lease_or_rent: prev?.lease_or_rent || '',
            contractor_name: cName || prev?.contractor_name || null,
            contractor_tin: cTin || prev?.contractor_tin || null,
            contractor_address: cAddr || prev?.contractor_address || null,
            commission_cpm: prev?.commission_cpm || null,
          }));
          setContractorDraft({
            contractor_name: cName,
            contractor_tin: cTin,
            contractor_address: cAddr,
          });
        }
      } else {
        setTaxInfo(emptyTax);
        setUseContractorOn1099(false);
      }

      // Resolve PAYER info via drivers.company_id → companies. Exact name
      // match first, then "first_name last_name" fallback. If the company's
      // payer columns haven't been deployed yet, fall back to base columns.
      const dr = await supabase
        .from('drivers')
        .select('first_name, last_name, company_id')
        .limit(200);
      const match = (dr.data || []).find((d: any) =>
        `${d.first_name || ''} ${d.last_name || ''}`.trim().toLowerCase() === driverName.trim().toLowerCase()
      );
      if (match?.company_id) {
        const cCols = 'id, name, address, city, state, zip_code, phone, payer_tin';
        let cRes: any = await supabase.from('companies').select(cCols).eq('id', match.company_id).maybeSingle();
        if (isMissingColumnErr(cRes.error)) {
          cRes = await supabase.from('companies').select('id, name, address, city, state, zip_code').eq('id', match.company_id).maybeSingle();
        }
        if (cRes.data) {
          const c: any = cRes.data;
          setPayerInfo({
            payer_tin: c.payer_tin || null,
            name: c.name || null,
            address: c.address || null,
            city: c.city || null,
            state: c.state || null,
            zip_code: c.zip_code || null,
            phone: c.phone || null,
          });
        } else {
          setPayerInfo(null);
        }
      } else {
        setPayerInfo(null);
      }

      // Scheduled-deductions entry for this driver — drives Owner Op vs
      // Company Driver classification and gives us the contractor business
      // name / address / TIN for the 1099-NEC PDF. Try the rich columns
      // first; fall back if contractor_tin's migration hasn't landed.
      try {
        let sdRes: any = await supabase
          .from('scheduled_deductions')
          .select('id, driver_name, lease_or_rent, contractor_name, contractor_address, contractor_tin, commission_cpm, created_at')
          .ilike('driver_name', driverName)
          .order('created_at', { ascending: false })
          .limit(1);
        if (isMissingColumnErr(sdRes.error)) {
          sdRes = await supabase
            .from('scheduled_deductions')
            .select('id, driver_name, lease_or_rent, contractor_name, contractor_address, commission_cpm, created_at')
            .ilike('driver_name', driverName)
            .order('created_at', { ascending: false })
            .limit(1);
        }
        const sdRow = (sdRes.data || [])[0] as any;
        if (sdRow) {
          // Merge sd values over any contractor info already seeded from
          // driver_1099_info so we don't wipe out tax-info values when the
          // sd row happens to have blanks.
          setContractorInfo(prev => ({
            id: sdRow.id,
            lease_or_rent: sdRow.lease_or_rent || prev?.lease_or_rent || '',
            contractor_name: sdRow.contractor_name || prev?.contractor_name || null,
            contractor_address: sdRow.contractor_address || prev?.contractor_address || null,
            contractor_tin: sdRow.contractor_tin || prev?.contractor_tin || null,
            commission_cpm: sdRow.commission_cpm || prev?.commission_cpm || null,
          }));
          setContractorDraft(d => ({
            contractor_name: sdRow.contractor_name || d.contractor_name,
            contractor_tin: sdRow.contractor_tin || d.contractor_tin,
            contractor_address: sdRow.contractor_address || d.contractor_address,
          }));
        } else {
          // No scheduled_deductions row. Keep whatever we seeded from
          // driver_1099_info so the Contractor card can still render.
          setContractorInfo(prev => prev ?? null);
        }
      } catch {
        setContractorInfo(prev => prev ?? null);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to load driver detail');
    } finally {
      setLoading(false);
    }
  }

  async function saveTaxInfo() {
    try {
      setSavingTax(true);
      const { data: { user } } = await supabase.auth.getUser();
      const city = taxInfo.city.trim();
      const state = taxInfo.state.trim().toUpperCase();
      const zip_code = taxInfo.zip_code.trim();
      // Also mirror a legacy city_state_zip string so older callers keep
      // working until the split-columns migration is deployed everywhere.
      const assembled = [city, state && zip_code ? `${state} ${zip_code}` : (state || zip_code)]
        .filter(Boolean).join(', ');
      const payload: Record<string, any> = {
        driver_name: driverName,
        recipient_tin: taxInfo.recipient_tin.trim() || null,
        street_address: taxInfo.street_address.trim() || null,
        city: city || null,
        state: state || null,
        zip_code: zip_code || null,
        city_state_zip: assembled || null,
        updated_by: user?.id || null,
        updated_at: new Date().toISOString(),
      };
      let upsertRes = await supabase
        .from('driver_1099_info')
        .upsert(payload, { onConflict: 'driver_name' });
      if (isMissingColumnErr(upsertRes.error)) {
        // Split columns not deployed yet — retry with the legacy payload only.
        const legacyPayload = {
          driver_name: payload.driver_name,
          recipient_tin: payload.recipient_tin,
          street_address: payload.street_address,
          city_state_zip: payload.city_state_zip,
          updated_by: payload.updated_by,
          updated_at: payload.updated_at,
        };
        upsertRes = await supabase
          .from('driver_1099_info')
          .upsert(legacyPayload, { onConflict: 'driver_name' });
      }
      if (upsertRes.error) throw upsertRes.error;
      toast.success('1099 info saved');
      setEditingTax(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSavingTax(false);
    }
  }

  const totals = useMemo(() => {
    let gross = 0;
    let deductions = 0;
    let fuel = 0;
    let reimbursements = 0; // total_credits acts as reimbursements/bonuses
    let net = 0;
    for (const r of statements) {
      gross += Number(r.total_revenue ?? r.net_pay ?? 0);
      deductions += Number(r.total_deductions || 0);
      fuel += Number(r.total_fuel || 0);
      reimbursements += Number(r.total_credits || 0);
      net += Number(r.net_pay || 0);
    }
    return {
      gross,
      deductions,
      fuel,
      reimbursements,
      expenses: deductions + fuel,
      net,
    };
  }, [statements]);
  const totalNet = totals.net;

  // Classification derived from the scheduled_deductions entry.
  //   'contractor'      → Accounting explicitly marked arrangement as Contractor
  //   owner/lease/etc.  → Owner Operator
  //   anything else     → Company Driver
  const OWNER_TYPES = new Set(['owner', 'owner_operator', 'lease', 'rent', 'buy']);
  const arrangement = (contractorInfo?.lease_or_rent || '').toLowerCase();
  const driverType: 'contractor' | 'owner_operator' | 'company_driver' =
    arrangement === 'contractor'
      ? 'contractor'
      : OWNER_TYPES.has(arrangement)
        ? 'owner_operator'
        : 'company_driver';
  const commissionPct = (() => {
    const raw = contractorInfo?.commission_cpm;
    if (!raw) return 0;
    const parsed = parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  })();

  async function openStatementPreview(row: DriverStatementRow) {
    if (!row.imported_file_path) {
      toast.error('No source PDF saved for this statement.');
      return;
    }
    try {
      setStatementPreviewLoading(row.id);
      const { data, error } = await supabase.storage
        .from('company-documents')
        .createSignedUrl(row.imported_file_path, 300);
      if (error) throw error;
      if (data?.signedUrl) {
        setStatementPreview({ url: data.signedUrl, row });
      }
    } catch (err: any) {
      toast.error(err?.message || 'Unable to open statement PDF');
    } finally {
      setStatementPreviewLoading(null);
    }
  }

  function closeStatementPreview() {
    setStatementPreview(null);
  }

  async function saveContractorInfo() {
    try {
      setSavingContractor(true);
      const { data: { user } } = await supabase.auth.getUser();
      // Save contractor identity on driver_1099_info (the 1099 table) so
      // imports don't pollute scheduled_deductions. If the driver already has
      // a scheduled_deductions row, mirror contractor_name/address there too
      // so the existing Scheduled Deductions UI stays consistent.
      const payload: Record<string, any> = {
        driver_name: driverName,
        contractor_name: contractorDraft.contractor_name.trim() || null,
        contractor_tin: contractorDraft.contractor_tin.trim() || null,
        contractor_address: contractorDraft.contractor_address.trim() || null,
        updated_by: user?.id || null,
        updated_at: new Date().toISOString(),
      };
      let res: any = await supabase
        .from('driver_1099_info')
        .upsert(payload, { onConflict: 'driver_name' });
      if (isMissingColumnErr(res.error)) {
        // contractor_* columns not yet deployed on driver_1099_info — retry
        // without them so at least the row exists with recipient fields.
        const { contractor_name: _n, contractor_tin: _t, contractor_address: _a, ...rest } = payload;
        void _n; void _t; void _a;
        res = await supabase.from('driver_1099_info').upsert(rest, { onConflict: 'driver_name' });
      }
      if (res.error) throw res.error;

      // Best-effort mirror to an existing scheduled_deductions row (never
      // create a new one — that pollutes the Scheduled Deductions page).
      if (contractorInfo?.id) {
        const mirror: Record<string, any> = {
          contractor_name: payload.contractor_name,
          contractor_address: payload.contractor_address,
        };
        let mr: any = await supabase
          .from('scheduled_deductions')
          .update({ ...mirror, contractor_tin: payload.contractor_tin })
          .eq('id', contractorInfo.id);
        if (isMissingColumnErr(mr.error)) {
          mr = await supabase.from('scheduled_deductions').update(mirror).eq('id', contractorInfo.id);
        }
        if (mr.error) console.warn('Could not mirror contractor info to scheduled_deductions:', mr.error);
      }

      toast.success('Contractor info saved');
      setEditingContractor(false);
      loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save contractor info');
    } finally {
      setSavingContractor(false);
    }
  }

  async function toggleUseContractor(next: boolean) {
    try {
      setSavingToggle(true);
      setUseContractorOn1099(next);
      const { data: { user } } = await supabase.auth.getUser();
      const payload = {
        driver_name: driverName,
        use_contractor_on_1099: next,
        updated_by: user?.id || null,
        updated_at: new Date().toISOString(),
      };
      let res: any = await supabase
        .from('driver_1099_info')
        .upsert(payload, { onConflict: 'driver_name' });
      if (isMissingColumnErr(res.error)) {
        // Column not in schema cache — fall back silently. The toggle still
        // works in the current session via local state; it just won't persist
        // until the migration is fully deployed.
        console.warn('use_contractor_on_1099 not persisted (schema cache):', res.error);
        return;
      }
      if (res.error) throw res.error;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save toggle');
    } finally {
      setSavingToggle(false);
    }
  }

  async function deleteStatement(row: DriverStatementRow) {
    const ok = window.confirm(
      `Delete statement for week ${fmtDate(row.week_start_date)} → ${fmtDate(row.week_end_date)}?\n\nThis removes the statement and its source PDF (if any) and cannot be undone.`
    );
    if (!ok) return;
    try {
      if (row.imported_file_path) {
        await supabase.storage.from('company-documents').remove([row.imported_file_path]).catch(() => { /* best effort */ });
      }
      // Release billed loads BEFORE deleting — line items cascade away with the
      // statement, and a load left marked as billed can never be paid.
      // Loads still billed by the driver's OTHER profile statement are kept marked.
      const released = await releaseStatementLoads([row.id]);
      const { error } = await supabase.from('driver_statements').delete().eq('id', row.id);
      if (error) throw error;
      toast.success(
        released > 0
          ? `Statement deleted — ${released} load${released === 1 ? '' : 's'} released for re-billing`
          : 'Statement deleted'
      );
      loadAll();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete statement');
    }
  }

  function downloadStatementPreview() {
    if (!statementPreview) return;
    const a = document.createElement('a');
    a.href = statementPreview.url;
    a.download = statementPreview.row.imported_file_name || `statement_${statementPreview.row.id.slice(0, 8)}.pdf`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function fmtDate(ymd: string) {
    // Parse "2025-05-12" as local midnight to avoid UTC off-by-one.
    const [y, m, d] = ymd.split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return ymd;
    return new Date(y, m - 1, d).toLocaleDateString();
  }

  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y--) out.push(y);
    return out;
  }, [currentYear]);

  async function openPreview() {
    if (statements.length === 0) return;
    try {
      setPdfLoading(true);
      // When the "Use contractor on 1099" checkbox is on and we actually have
      // a business name / TIN, swap them in as the RECIPIENT. Contractor
      // address (if present) goes on the street line; city/state/zip stay
      // whatever is in Tax Information so Accounting can keep one source.
      const useContractor = useContractorOn1099 && !!contractorInfo?.contractor_name;
      const recipientLabel = useContractor
        ? (contractorInfo!.contractor_name as string)
        : driverName;
      const pdfTaxInfo = useContractor
        ? {
            ...taxInfo,
            recipient_tin: contractorInfo?.contractor_tin || taxInfo.recipient_tin,
            street_address: contractorInfo?.contractor_address || taxInfo.street_address,
          }
        : taxInfo;
      const bytes = await build1099NecPdfBytes({
        driverLabel: recipientLabel,
        year,
        totalPaid: totals.gross,
        taxInfo: pdfTaxInfo,
        payerInfo,
      });
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfPreview({ label: recipientLabel, url, bytes });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to build 1099 PDF preview');
    } finally {
      setPdfLoading(false);
    }
  }

  function closePreview() {
    if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  }

  function downloadPreview() {
    if (!pdfPreview) return;
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    a.download = `1099_NEC_${year}_${driverName.replace(/\s+/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function back() {
    if (onBack) return onBack();
    window.history.pushState({}, '', '/company-1099');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  const fmtMoney = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6 p-6">
      <button onClick={back} className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to 1099 summary
      </button>

      {/* Header: name + type badge + statement count + year picker */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-bold text-slate-900">{driverName}</h2>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            driverType === 'contractor'
              ? 'bg-purple-100 text-purple-800 border border-purple-200'
              : driverType === 'owner_operator'
                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
            {driverType === 'contractor' ? 'Contractor' : driverType === 'owner_operator' ? 'Owner Operator' : 'Company Driver'}
          </span>
          <span className="text-sm text-slate-500">
            {statements.length} statement{statements.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wide text-slate-500">Tax Year</label>
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-900 bg-white"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {/* Tax Information card */}
          <div className="bg-white rounded-lg shadow border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Tax Information (1099-NEC)</h3>
              {!editingTax ? (
                <button
                  onClick={() => setEditingTax(true)}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                >
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditingTax(false); loadAll(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                  <button
                    onClick={saveTaxInfo}
                    disabled={savingTax}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingTax ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Recipient's TIN (Tax ID Number)</label>
                {editingTax ? (
                  <input
                    type="text"
                    value={taxInfo.recipient_tin}
                    onChange={e => setTaxInfo(s => ({ ...s, recipient_tin: e.target.value }))}
                    placeholder="e.g. 12-3456789 or 123-45-6789"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-slate-900">{taxInfo.recipient_tin || <span className="text-slate-400 italic">Not set</span>}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Street address (including apt. no.)</label>
                {editingTax ? (
                  <input
                    type="text"
                    value={taxInfo.street_address}
                    onChange={e => setTaxInfo(s => ({ ...s, street_address: e.target.value }))}
                    placeholder="e.g. 1673 Bay Rd apt 302"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-slate-900">{taxInfo.street_address || <span className="text-slate-400 italic">Not set</span>}</p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_6rem_8rem] gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">City</label>
                  {editingTax ? (
                    <input
                      type="text"
                      value={taxInfo.city}
                      onChange={e => setTaxInfo(s => ({ ...s, city: e.target.value }))}
                      placeholder="e.g. Miami"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-slate-900">{taxInfo.city || <span className="text-slate-400 italic">Not set</span>}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">State</label>
                  {editingTax ? (
                    <input
                      type="text"
                      value={taxInfo.state}
                      maxLength={2}
                      onChange={e => setTaxInfo(s => ({ ...s, state: e.target.value.toUpperCase() }))}
                      placeholder="FL"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm uppercase"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-slate-900">{taxInfo.state || <span className="text-slate-400 italic">—</span>}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">ZIP</label>
                  {editingTax ? (
                    <input
                      type="text"
                      value={taxInfo.zip_code}
                      onChange={e => setTaxInfo(s => ({ ...s, zip_code: e.target.value }))}
                      placeholder="33139"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-slate-900">{taxInfo.zip_code || <span className="text-slate-400 italic">—</span>}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contractor / Owner Operator card — always visible so Accounting
              can add a business name / TIN / address even for drivers without
              a scheduled_deductions row. */}
          {(() => { const ci = contractorInfo || { lease_or_rent: '' }; return (
            <div className="bg-white rounded-lg shadow border border-slate-200">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 gap-4 flex-wrap">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-slate-800">Contractor / Owner Operator Info</h3>
                  <p className="text-xs text-slate-500">Pulled from this driver's scheduled deductions.</p>
                </div>
                <div className="flex items-center gap-3">
                  {!editingContractor ? (
                    <button
                      onClick={() => setEditingContractor(true)}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                    >
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingContractor(false); loadAll(); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                      >
                        <X className="w-4 h-4" /> Cancel
                      </button>
                      <button
                        onClick={saveContractorInfo}
                        disabled={savingContractor}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingContractor ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle: use contractor on 1099 */}
              <label className="flex items-start gap-3 px-4 py-3 border-b border-slate-200 bg-blue-50/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useContractorOn1099}
                  onChange={(e) => toggleUseContractor(e.target.checked)}
                  disabled={savingToggle || !ci.contractor_name}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    Use Contractor name &amp; TIN on 1099-NEC
                  </p>
                  <p className="text-xs text-slate-600">
                    When on, the 1099-NEC's <span className="font-semibold">RECIPIENT</span> is the business
                    {ci.contractor_name ? <> (<span className="font-semibold">{ci.contractor_name}</span>)</> : null}
                    {' '}instead of the driver's personal name. The Contractor TIN and address override
                    the driver's Tax Information on the printed form.
                  </p>
                  {!ci.contractor_name && (
                    <p className="text-xs text-amber-700 mt-1">
                      Add a Business / Contractor name below before turning this on.
                    </p>
                  )}
                </div>
              </label>

              <div className="p-4 space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Business / Contractor name</label>
                  {editingContractor ? (
                    <input
                      type="text"
                      value={contractorDraft.contractor_name}
                      onChange={e => setContractorDraft(d => ({ ...d, contractor_name: e.target.value }))}
                      placeholder="e.g. Martinez & Son Trucking LLC"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    />
                  ) : (
                    <p className="mt-1 text-sm font-semibold text-slate-900 truncate">
                      {ci.contractor_name || <span className="text-slate-400 italic">Not set</span>}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Contractor TIN (EIN)</label>
                  {editingContractor ? (
                    <input
                      type="text"
                      value={contractorDraft.contractor_tin}
                      onChange={e => setContractorDraft(d => ({ ...d, contractor_tin: e.target.value }))}
                      placeholder="e.g. 12-3456789"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    />
                  ) : (
                    <p className="mt-1 text-sm font-semibold text-slate-900 font-mono">
                      {ci.contractor_tin || <span className="text-slate-400 italic font-sans">Not set</span>}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Contractor address</label>
                  {editingContractor ? (
                    <input
                      type="text"
                      value={contractorDraft.contractor_address}
                      onChange={e => setContractorDraft(d => ({ ...d, contractor_address: e.target.value }))}
                      placeholder="Street, City, State ZIP"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-slate-900">
                      {ci.contractor_address || <span className="text-slate-400 italic">Not set</span>}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ); })()}

          {/* Three stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Gross Pay</p>
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="mt-2 text-2xl font-bold text-emerald-700">{fmtMoney(totals.gross)}</p>
            </div>
            <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Expenses</p>
                <TrendingDown className="w-5 h-5 text-rose-600" />
              </div>
              <p className="mt-2 text-2xl font-bold text-rose-700">{fmtMoney(totals.expenses)}</p>
            </div>
            <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Net Pay</p>
                <Wallet className="w-5 h-5 text-blue-600" />
              </div>
              <p className="mt-2 text-2xl font-bold text-blue-700">{fmtMoney(totals.net)}</p>
            </div>
          </div>

          {/* Total Expense Breakdown + Generate 1099-NEC */}
          <div className="bg-white rounded-lg shadow border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Total Expense Breakdown</h3>
              <button
                onClick={openPreview}
                disabled={statements.length === 0 || pdfLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                Generate 1099-NEC
              </button>
            </div>
            <div className="p-4 divide-y divide-slate-100">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-700">Gross Pay (1099 Amount)</span>
                <span className="text-sm font-semibold text-emerald-700">{fmtMoney(totals.gross)}</span>
              </div>
              {driverType !== 'company_driver' && commissionPct > 0 && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-700">After {commissionPct}% commission</span>
                  <span className="text-sm font-semibold text-emerald-700">{fmtMoney(totals.gross * (1 - commissionPct / 100))}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-700">Deductions</span>
                <span className="text-sm font-semibold text-rose-700">-{fmtMoney(totals.deductions)}</span>
              </div>
              {totals.fuel > 0 && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-700">Fuel</span>
                  <span className="text-sm font-semibold text-rose-700">-{fmtMoney(totals.fuel)}</span>
                </div>
              )}
              {totals.reimbursements > 0 && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-700">Reimbursements Total</span>
                  <span className="text-sm font-semibold text-rose-700">-{fmtMoney(totals.reimbursements)}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-semibold text-slate-900">Net Pay</span>
                <span className="text-sm font-bold text-blue-700">{fmtMoney(totals.net)}</span>
              </div>
            </div>
          </div>

          {/* Weekly Statements */}
          <div className="bg-white rounded-lg shadow border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">
                Weekly Statements ({statements.length})
              </h3>
            </div>
            {statements.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                No statements for {driverName} in {year}.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {statements.map(s => {
                  const gross = Number(s.total_revenue ?? s.net_pay ?? 0);
                  const deductions = Number(s.total_deductions || 0);
                  const fuel = Number(s.total_fuel || 0);
                  const reimbursements = Number(s.total_credits || 0);
                  const expenses = deductions + fuel;
                  const net = Number(s.net_pay || 0);
                  const sourceLabel = s.imported_file_name
                    ? s.imported_file_name
                    : `Statement #${s.id.slice(0, 8)} — Unit #${s.truck_number}`;
                  return (
                    <div key={s.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">
                              Week: {fmtDate(s.week_start_date)} → {fmtDate(s.week_end_date)}
                            </span>
                            {s.imported_file_name && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                                <Sparkles className="w-3 h-3" /> Imported
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            <span className="text-slate-600">
                              Net: <span className="font-semibold text-blue-700">{fmtMoney(net)}</span>
                            </span>
                            <span className="text-slate-600">
                              Gross Pay: <span className="font-semibold text-emerald-700">{fmtMoney(gross)}</span>
                            </span>
                            <span className="text-slate-600">
                              Expenses: <span className="font-semibold text-rose-700">{fmtMoney(expenses)}</span>
                            </span>
                          </div>
                          {(deductions > 0 || fuel > 0 || reimbursements > 0) && (
                            <div className="mt-2 text-xs space-y-0.5">
                              {deductions > 0 && (
                                <div className="flex items-center gap-2 text-slate-600">
                                  <span>Deductions</span>
                                  <span className="font-semibold text-rose-700">-{fmtMoney(deductions)}</span>
                                </div>
                              )}
                              {fuel > 0 && (
                                <div className="flex items-center gap-2 text-slate-600">
                                  <span>Fuel</span>
                                  <span className="font-semibold text-rose-700">-{fmtMoney(fuel)}</span>
                                </div>
                              )}
                              {reimbursements > 0 && (
                                <div className="flex items-center gap-2 text-slate-600">
                                  <span>Reimbursements Total</span>
                                  <span className="font-semibold text-rose-700">-{fmtMoney(reimbursements)}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-2 text-xs text-slate-500 truncate">
                            {sourceLabel}
                            <span className="mx-1.5">•</span>
                            Uploaded {new Date(s.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {s.imported_file_path ? (
                            <button
                              onClick={() => openStatementPreview(s)}
                              disabled={statementPreviewLoading === s.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-white text-slate-700 text-xs font-medium disabled:opacity-50"
                            >
                              {statementPreviewLoading === s.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Download className="w-3.5 h-3.5" />}
                              Preview
                            </button>
                          ) : null}
                          <button
                            onClick={() => deleteStatement(s)}
                            title="Delete statement"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* 1099-NEC preview modal — renders the filled PDF in an iframe */}
      {pdfPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">1099-NEC preview</h3>
                <p className="text-xs text-slate-500">
                  {pdfPreview.label} · {year} · Total ${totals.gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <button onClick={closePreview} className="p-1 rounded hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden bg-slate-100">
              <iframe
                src={pdfPreview.url}
                title="1099-NEC preview"
                className="w-full h-full border-0"
              />
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button
                onClick={closePreview}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={downloadPreview}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <FileDown className="w-4 h-4" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-statement PDF preview modal */}
      {statementPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900 truncate">
                  {statementPreview.row.imported_file_name || `Statement ${statementPreview.row.id.slice(0, 8)}`}
                </h3>
                <p className="text-xs text-slate-500">
                  {fmtDate(statementPreview.row.week_start_date)} → {fmtDate(statementPreview.row.week_end_date)}
                  <span className="mx-1.5">•</span>
                  Net ${Number(statementPreview.row.net_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <button onClick={closeStatementPreview} className="p-1 rounded hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden bg-slate-100">
              <iframe
                src={statementPreview.url}
                title="Statement preview"
                className="w-full h-full border-0"
              />
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button
                onClick={closeStatementPreview}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-white"
              >
                Close
              </button>
              <button
                onClick={downloadStatementPreview}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
